import { isEmpty } from 'lodash'
import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import { useLanguageStore } from '../language/use-language-store'
import { useBrowserStore } from '../browser-store/use-browser-store'
import { useErrorStore } from '../error-boundary/use-error-boundary-store'
import { store } from '../../redux-store'
import {
  login,
  logout,
  showMyProjects
} from '../../containers/authentication/actions'
import {
  getUser,
  getUserId
} from '../../react-query/authentication/queries/use-me-query'
import { customQueryClient } from '../../react-query/config/query-client'
import { useMtlsCertificateStore } from '../mtls-certificate/use-mtls-certificate-store.jsx'

const getTokenFromStorage = () => {
  if (typeof localStorage !== 'undefined') {
    const tokenSource = localStorage.getItem('dtToken')
    if (!tokenSource) return null

    const token = JSON.parse(tokenSource)
    if (new Date(Number(token.expires_at)) > Date.now()) {
      return token
    }

    localStorage.removeItem('dtToken')
  }
  return null
}

// Middleware
const authStoreMiddleware = (config) => (set, get, api) => {
  const originalSetToken = config(set, get, api).setToken
  const originalLogout = config(set, get, api).logout
  const originalLoginSuccessful = config(set, get, api).loginSuccessful
  const originalSetHideMyProjects = config(set, get, api).setHideMyProjects
  const originalSetShowMyProjects = config(set, get, api).setShowMyProjects

  return {
    ...config(set, get, api),

    setToken: (token) => {
      if (token && typeof localStorage !== 'undefined') {
        token.expires_at = Date.now() + Number(token.expires_in) * 1000

        localStorage.setItem('dtToken', JSON.stringify(token))
      }
      originalSetToken(token)
    },

    loginSuccessful: (token) => {
      if (token && typeof localStorage !== 'undefined') {
        token.expires_at = Date.now() + Number(token.expires_in) * 1000

        localStorage.setItem('dtToken', JSON.stringify(token))
      }

      // dispatch to reset all reduxes ( should be removed after full refactor. )
      const { dispatch } = store
      dispatch(login())

      originalLoginSuccessful(token)
    },

    logout: () => {
      const { token } = get()
      if (typeof sessionStorage !== 'undefined') {
        if (sessionStorage) {
          sessionStorage.removeItem('language')
        }
        if (token) {
          localStorage.setItem('docuToolsLogout', Date.now())
          localStorage.removeItem('dtToken')
        }
      }

      // reseting other stores
      useMtlsCertificateStore.getState().logout()
      useLanguageStore.getState().logout()
      useBrowserStore.getState().logout()
      useErrorStore.getState().logout()

      // dispatch to reset all reduxes ( should be removed after full refactor. )
      const { dispatch } = store
      dispatch(logout())

      originalLogout()

      // Clear react-query Cache
      customQueryClient.clear()
    },

    setHideMyProjects: () => {
      const userId = getUserId()
      if (userId && typeof localStorage !== 'undefined') {
        localStorage.setItem(`hideMyProjects-${userId}`, 'true')
      }
      originalSetHideMyProjects()
    },

    setShowMyProjects: () => {
      const userId = getUserId()
      if (userId && typeof localStorage !== 'undefined') {
        localStorage.removeItem(`hideMyProjects-${userId}`)
      }

      // dispatch to reset all reduxes ( should be removed after full refactor. )
      const { dispatch } = store
      dispatch(showMyProjects())
      originalSetShowMyProjects()
    }
  }
}

const initialState = {
  expiredToken: false,
  showLoginPopup: false,
  initializedApp: false,
  passwordPolicy: null,
  show2FA: null,
  hideMyProjects: false,
  serviceFlags: null,
  token: getTokenFromStorage()
}

export const useAuthStore = create(
  authStoreMiddleware((set, get) => ({
    ...initialState,
    setExpiredToken: (expiredToken) => set({ expiredToken }),

    setShowLoginPopup: (showLoginPopup) => set({ showLoginPopup }),

    setInitializedApp: (initializedApp) => set({ initializedApp }),

    setToken: (token) => set({ token }),
    setPasswordPolicy: (passwordPolicy) => set({ passwordPolicy }),

    setShow2FA: (show2FA) => set({ show2FA }),

    loginSuccessful: (token) =>
      set({
        token,
        show2FA: undefined,
        expiredToken: false,
        showLoginPopup: false
      }),

    logout: () =>
      set((state) => ({
        ...initialState,
        token: null,
        initializedApp: state.initializedApp
      })),

    setHideMyProjects: () => set({ hideMyProjects: true }),
    setShowMyProjects: () => set({ hideMyProjects: false })
  })),
  shallow
)

// Selectors
export const useToken = () => useAuthStore((state) => state.token, shallow)

export const useSetPasswordPolicy = () =>
  useAuthStore((state) => state.setPasswordPolicy, shallow)

export const useIsLoggedIn = () => {
  const token = useToken()
  const user = getUser()
  return !isEmpty(token) && !isEmpty(user)
}

export const useHeaders = () => {
  const token = useToken()

  if (token) {
    return {
      Authorization: `${token.token_type} ${token.access_token}`
    }
  }
  return {}
}

export const useAccessToken = () => {
  const token = useToken()

  if (token && token.access_token) {
    return token.access_token
  }
  return null
}

export const useRefreshToken = () => {
  const token = useToken()

  if (token && token?.refresh_token) {
    return token.refresh_token
  }

  return null
}

export const useAccessSearchParam = () => {
  const token = useToken()

  const accessToken = token?.access_token ?? null

  return (notFirst = false) =>
    accessToken ? `${!notFirst ? '?' : '&'}access_token=${accessToken}` : ''
}

export const useTokenAvailable = () => {
  const token = useToken()

  if (!isEmpty(token)) {
    return true
  }
  return false
}

// STATIC
export const getToken = () => {
  return useAuthStore.getState().token ?? null
}

export const getIsLoggedInStatic = () => {
  const token = getToken()
  const user = getUser()

  return !isEmpty(token) && !isEmpty(user)
}

export const getHeadersStatic = () => {
  const token = getToken()
  return token
    ? {
        Authorization: `${token.token_type} ${token.access_token}`
      }
    : {}
}

export const getAccessTokenStatic = () => {
  return getToken()?.access_token ?? null
}

export const getRefreshTokenStatic = () => {
  return getToken()?.refresh_token ?? null
}

export const getAccessSearchParamStatic = (notFirst = false) => {
  const accessToken = getToken()?.access_token
  return accessToken
    ? `${!notFirst ? '?' : '&'}access_token=${accessToken}`
    : ''
}

export const getTokenAvailableStatic = () => {
  return !isEmpty(getToken())
}
